import React, { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { cn } from '@/lib/utils';
import { DraperyModule } from '../../redux';

type PartsAndSuppliesPricingTableProps = {
  rows: any[];
  shippingRow: any;
  includeShipping: boolean;
  disabled?: boolean;
  showItemColumn?: boolean;
  onChange(
    index: number,
    field: 'item' | 'estimateDescription' | 'cost' | 'quantity' | 'markup' | 'customerPrice',
    value: string,
    isShipping?: boolean
  ): void;
  onToggleShipping(include: boolean): void;
  draperyOrderHardware: DraperyModule.DraperyOrderHardware;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});
const COLON_VALIDATION_MESSAGE = 'Cannot contain :';

const parseNumber = (value?: string | number) => {
  if (value === null || value === undefined || value === '') return NaN;
  const num = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(num) ? num : NaN;
};

const toNumber = (value?: string | number) => {
  const parsed = parseNumber(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const computeCalculatedValue = (
  row: any,
  draperyOrderHardware: DraperyModule.DraperyOrderHardware
): number => {
  const quantity = toNumber(row?.quantity);
  const cost = toNumber(row?.cost);
  const markup = toNumber(row?.markup);

  // additional price adjustment
  const additionalPriceAdjustment =
    draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType || 'none';
  // additional price adjustment value
  const additionalPriceAdjustmentValue =
    Number(draperyOrderHardware?.hardwareAdditionalPriceAdjustment) || 0;
  // additional price adjustment (discount or markup)
  const additionalPriceAdjustmentCalculate =
    additionalPriceAdjustment === 'discount'
      ? -additionalPriceAdjustmentValue
      : additionalPriceAdjustmentValue;

  // Total sum of client additional price adjustment and markup
  const totalPercentage = markup / 100 + additionalPriceAdjustmentCalculate / 100;

  return quantity * cost * (1 + totalPercentage);
};

const formatCurrency = (value: number) => currencyFormatter.format(value);

export const PartsAndSuppliesPricingTable = ({
  rows,
  shippingRow,
  includeShipping,
  disabled,
  showItemColumn = false,
  onChange,
  onToggleShipping,
  draperyOrderHardware,
}: PartsAndSuppliesPricingTableProps) => {
  const [itemFieldErrors, setItemFieldErrors] = useState<Record<string, string>>({});

  const totals = useMemo(() => {
    const rowsToSum = includeShipping ? [...rows, shippingRow] : rows;
    return rowsToSum.reduce(
      (acc, row) => {
        // additional price adjustment
        const additionalPriceAdjustment =
          draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType || 'none';
        // additional price adjustment value
        const additionalPriceAdjustmentValue =
          Number(draperyOrderHardware?.hardwareAdditionalPriceAdjustment) || 0;
        // additional price adjustment (discount or markup)
        const additionalPriceAdjustmentCalculate =
          additionalPriceAdjustment === 'discount'
            ? -additionalPriceAdjustmentValue
            : additionalPriceAdjustmentValue;

        const totalPercentage =
          toNumber(row?.markup) / 100 + additionalPriceAdjustmentCalculate / 100;

        const baseCost = toNumber(row?.quantity) * toNumber(row?.cost);
        const calculated = baseCost * (1 + totalPercentage);
        const customerRaw = parseNumber(row?.customerPrice);
        const customerValue = Number.isNaN(customerRaw) ? calculated : customerRaw;
        acc.cost += baseCost;
        acc.calculated += calculated;
        acc.customer += customerValue;
        return acc;
      },
      { cost: 0, calculated: 0, customer: 0 }
    );
  }, [rows, shippingRow, includeShipping]);

  const inputBase =
    'h-10 rounded-md border border-[#dbe3ee] bg-white px-3 text-[13px] text-[#344054] shadow-[0_1px_2px_rgba(16,24,40,0.04)] focus-visible:border-[#8bc2db] focus-visible:ring-0 disabled:opacity-100 disabled:bg-[#f2f4f7] disabled:text-[#98a2b3]';

  const getRowKey = (index: number, isShipping?: boolean) =>
    `${isShipping ? 'shipping' : 'main'}-${index}`;

  const clearRowItemError = (rowKey: string) => {
    setItemFieldErrors((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const renderRow = (row: any, index: number, isShipping?: boolean) => {
    const calculatedValue = computeCalculatedValue(row, draperyOrderHardware);
    const calculatedDisplay = formatCurrency(calculatedValue);
    const customerPrice =
      row?.customerPrice !== undefined && row?.customerPrice !== null && row?.customerPrice !== ''
        ? row.customerPrice
        : calculatedValue.toFixed(2);
    const rowDisabled = disabled || (isShipping && !includeShipping);
    const rowKey = getRowKey(index, isShipping);

    const shippingItemLabel = 'Shipping & Handling';
    const itemHasColon = typeof row?.item === 'string' && row.item.includes(':');
    const showItemError = !isShipping && !!(itemFieldErrors[rowKey] || itemHasColon);

    return (
      <TableRow
        key={isShipping ? 'shipping-row' : row?.id || `headrail-row-${index}`}
        className={cn('border-[#e5e7eb] bg-white', rowDisabled && 'opacity-70')}
      >
        {showItemColumn && (
          <TableCell className="w-[180px] px-4 py-3">
            <div className="flex flex-col gap-1">
              <Input
                value={isShipping ? shippingItemLabel : row?.item || ''}
                onChange={(e) => {
                  if (isShipping) {
                    onChange(index, 'item', e.target.value, isShipping);
                    return;
                  }

                  const nextValue = e.target.value || '';
                  const prevValue = String(row?.item || '');
                  const nextColonCount = (nextValue.match(/:/g) || []).length;
                  const prevColonCount = (prevValue.match(/:/g) || []).length;
                  const isAddingColon = nextColonCount > prevColonCount;

                  if (isAddingColon) {
                    setItemFieldErrors((prev) => ({
                      ...prev,
                      [rowKey]: COLON_VALIDATION_MESSAGE,
                    }));
                    return;
                  }

                  if (!nextValue.includes(':')) {
                    clearRowItemError(rowKey);
                  }

                  onChange(index, 'item', nextValue, isShipping);
                }}
                placeholder="Item"
                disabled={rowDisabled || isShipping}
                className={cn(inputBase, showItemError && 'border-[#e11900] focus-visible:border-[#e11900]')}
              />
              {showItemError && (
                <span className="text-[12px] leading-[1.2] text-[#e11900]">
                  {COLON_VALIDATION_MESSAGE}
                </span>
              )}
            </div>
          </TableCell>
        )}
        <TableCell className="w-[36%] px-4 py-3">
          <Input
            value={row?.estimateDescription || ''}
            onChange={(e) => onChange(index, 'estimateDescription', e.target.value, isShipping)}
            placeholder={isShipping ? 'Shipping & Handling' : 'Estimate description'}
            disabled={rowDisabled}
            className={inputBase}
          />
        </TableCell>
        <TableCell className="w-[90px] px-4 py-3">
          <Input
            type="number"
            value={row?.quantity || ''}
            onChange={(e) => onChange(index, 'quantity', e.target.value, isShipping)}
            inputMode="decimal"
            min={0}
            step="any"
            placeholder="0"
            disabled={rowDisabled || isShipping}
            className={cn(inputBase, 'text-right')}
          />
        </TableCell>
        <TableCell className="w-[140px] px-4 py-3">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#667085]">
              $
            </span>
            <Input
              value={row?.cost || ''}
              onChange={(e) => onChange(index, 'cost', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className={cn(inputBase, 'pl-6', 'text-right')}
            />
          </div>
        </TableCell>
        <TableCell className="w-[140px] px-4 py-3">
          <div className="relative">
            <Input
              value={row?.markup || ''}
              onChange={(e) => onChange(index, 'markup', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className={cn(inputBase, 'pr-8', 'text-right')}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#667085]">
              %
            </span>
          </div>
        </TableCell>
        {draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType &&
        draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType !== 'none' ? (
          <TableCell className="p-3 text-center text-sm">
            {draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType === 'discount' && '-'}
            {draperyOrderHardware?.hardwareAdditionalPriceAdjustment || '0'}%
          </TableCell>
        ) : null}
        <TableCell className="w-[170px] px-4 py-3">
          <div
            className={cn(
              'flex h-9 items-center justify-end rounded-md border border-[#d7dee8] bg-[#f8fafc] px-3 text-[13px] font-semibold text-[#101828]',
              rowDisabled && 'bg-[#f2f4f7] text-[#98a2b3]'
            )}
          >
            {calculatedDisplay}
          </div>
        </TableCell>
        <TableCell className="w-[200px] px-4 py-3">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#667085]">
              $
            </span>
            <Input
              value={customerPrice}
              onChange={(e) => onChange(index, 'customerPrice', e.target.value, isShipping)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={rowDisabled}
              className={cn(inputBase, 'pl-6', 'text-right')}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="rounded-lg border border-[#e5e7eb] bg-white">
      <Table className={cn(showItemColumn ? 'min-w-[1120px]' : 'min-w-[960px]')}>
        <TableHeader>
          <TableRow className="border-[#e5e7eb] bg-[#eef3f6] text-[#667085]">
            {showItemColumn && (
              <TableHead className="w-[180px] px-4 text-[13px] font-semibold text-[#334155]">
                Item
              </TableHead>
            )}
            <TableHead className="px-4 text-[13px] font-semibold text-[#334155]">
              Estimate Description
            </TableHead>
            <TableHead className="w-[90px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Quantity
            </TableHead>
            <TableHead className="w-[140px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Cost
            </TableHead>
            <TableHead className="w-[140px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Product Markup
            </TableHead>
            {draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType &&
              draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType !== 'none' && (
                <TableHead className="w-[140px] px-4 text-center text-[13px] font-semibold text-[#334155]">
                  Client Price Adjustment
                </TableHead>
              )}
            <TableHead className="w-[170px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Calculated Price
            </TableHead>
            <TableHead className="w-[200px] px-4 text-center text-[13px] font-semibold text-[#334155]">
              Customer-Facing Price
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {rows.map((row, index) => renderRow(row, index))}

          {includeShipping && renderRow(shippingRow, 0, true)}

          <TableRow className="border-[#e5e7eb] bg-white">
            <TableCell
              colSpan={
                showItemColumn
                  ? 4
                  : draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType &&
                      draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType !== 'none'
                    ? 3
                    : 2
              }
              className="px-4 py-3"
            >
              <div className="flex items-center gap-3 text-[13px] font-medium text-[#344054]">
                <Switch
                  checked={includeShipping}
                  onClick={() => !disabled && onToggleShipping(!includeShipping)}
                  disabled={disabled}
                  aria-label="Toggle shipping and handling for parts and supplies"
                />
                <span>Add Shipping & Handling</span>
              </div>
            </TableCell>
            <TableCell
              colSpan={
                draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType &&
                draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType !== 'none'
                  ? 2
                  : 1
              }
              className="px-4 py-3 text-right text-[13px] font-semibold text-[#111827]"
            >
              TOTAL PRICE
            </TableCell>
            <TableCell className="px-4 py-3 text-right text-[13px] font-semibold text-[#111827]">
              {formatCurrency(totals.calculated)}
            </TableCell>
            <TableCell className="px-4 py-3 text-right text-[13px] font-semibold text-[#111827]">
              {formatCurrency(totals.customer)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};
